import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Autocomplete,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
    Typography,
} from "@mui/material";
import { Button } from "@foodpilot/foods";
import { usePuckExtensionManagementOptions } from "src/components/PuckExtensions";
import type { PuckExtension } from "src/api/client-api/puckExtensions/useExtensions";
import { cmsAutocompleteEnterSelectProps } from "src/utils/autocomplete";
import { getSectorDisplayName, mergeRoleOptions, mergeSectorOptions } from "../dynablockSettings";

type DynaBlockSettingsDialogProps = {
    open: boolean;
    extension: PuckExtension | null;
    onClose: () => void;
    onSave: (extensionId: number, allowedRoles: string[], sectorIris: string[]) => void;
    isSaving: boolean;
};

export const DynaBlockSettingsDialog = (props: DynaBlockSettingsDialogProps) => {
    const { open, extension, onClose, onSave, isSaving } = props;
    const { t } = useTranslation();
    const { data: managementOptions, isLoading: areOptionsLoading } = usePuckExtensionManagementOptions();
    const roles = managementOptions?.roles ?? [];
    const sectors = managementOptions?.sectors ?? [];

    const [selectedRoles, setSelectedRoles] = useState<string[]>(() => extension?.allowedRoles ?? []);
    const [selectedSectorIris, setSelectedSectorIris] = useState<string[]>(() => extension?.sectors ?? []);

    // Reset roles when extension changes
    useEffect(() => {
        setSelectedRoles(extension?.allowedRoles ?? []);
    }, [extension]);

    useEffect(() => {
        setSelectedSectorIris(extension?.sectors ?? []);
    }, [extension]);

    const roleOptions = mergeRoleOptions(
        selectedRoles,
        roles.map((role) => role.code),
    );
    const sectorOptions = mergeSectorOptions(selectedSectorIris, sectors);
    const getRoleDisplayName = (roleCode: string) => roles.find((role) => role.code === roleCode)?.name ?? roleCode;

    const handleSave = () => {
        if (!extension) return;
        onSave(extension.id, selectedRoles, selectedSectorIris);
    };

    const handleClose = () => {
        if (!isSaving) onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t("common.actions.settings", "Settings")}
                {extension && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {extension.name}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Autocomplete
                        {...cmsAutocompleteEnterSelectProps}
                        multiple
                        options={roleOptions}
                        value={selectedRoles}
                        onChange={(_, values) => setSelectedRoles(values)}
                        getOptionLabel={getRoleDisplayName}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("settings.cms.pages.roles.label", "Allowed Roles")}
                                margin="dense"
                                helperText={areOptionsLoading ? t("common.loading", "Loading...") : undefined}
                            />
                        )}
                        disabled={isSaving || areOptionsLoading}
                    />

                    <Autocomplete
                        {...cmsAutocompleteEnterSelectProps}
                        multiple
                        options={sectorOptions}
                        getOptionLabel={(sectorIri) => getSectorDisplayName(sectorIri, sectors)}
                        value={selectedSectorIris}
                        onChange={(_, values) => setSelectedSectorIris(values)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("settings.cms.pages.create_dialog.sector", "Sector (optional)")}
                                margin="dense"
                                helperText={areOptionsLoading ? t("common.loading", "Loading...") : undefined}
                            />
                        )}
                        disabled={isSaving || areOptionsLoading}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="text" onClick={handleClose} disabled={isSaving}>
                    {t("common.cancel", "Cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
                >
                    {isSaving ? t("common.loading", "Loading...") : t("common.actions.save", "Save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
